import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuX } from "react-icons/lu";
import { AdminInput } from "../../../../../components/adminForm";
import {
  getDiscounts,
  newDiscount,
  updateDiscount,
} from "../../../../../api/discounts";
import {
  requestTableRefresh,
  updateFloatPanel,
} from "../../../../../reducers/userConfigSlice";
import { EditorDialog } from "../../products/edition/editorDialog";
import "../../products/edition/productEditorPage.css";
import { discountDraft, discountPayload, discountMatches } from "./model";
export const DiscountEditor = () => {
  const panel = useSelector((s) => s.aplicationConfig.userConfig.floatPanel),
    dispatch = useDispatch();
  const [row, setRow] = useState(null),
    [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null);
  const [id, setId] = useState(panel.isEmpty ? null : panel.queryId),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false),
    [unknown, setUnknown] = useState(false),
    [discard, setDiscard] = useState(false),
    [retry, setRetry] = useState(0);
  const lock = useRef(false),
    pending = useRef(null);
  const dirty = form && JSON.stringify(form) !== JSON.stringify(baseline);
  const read = async (target) => {
    const response = await getDiscounts();
    const found =
      response?.error === false &&
      Array.isArray(response.data) &&
      response.data.find((r) => r.discountId === target);
    if (!found) throw Error("No se ha podido cargar el descuento.");
    return found;
  };
  useEffect(() => {
    let current = true;
    setError("");
    if (panel.isEmpty) {
      const next = discountDraft(null);
      setForm(next);
      setBaseline(next);
      return;
    }
    read(panel.queryId)
      .then((result) => {
        if (!current) return;
        const next = discountDraft(result);
        setRow(result);
        setForm(next);
        setBaseline(next);
      })
      .catch(() => {
        if (current)
          setError("No se ha podido cargar el descuento. Vuelve a intentarlo.");
      });
    return () => {
      current = false;
    };
  }, [panel.queryId, panel.isEmpty, retry]);
  useEffect(() => {
    const warn = (e) => {
      if (dirty || busy) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, busy]);
  const finish = () =>
    dispatch(
      updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
    );
  const close = () => {
    if (lock.current) return;
    if (dirty) setDiscard(true);
    else finish();
  };
  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const confirm = async (target, expected) => {
    const result = await read(target);
    if (!discountMatches(result, expected))
      throw Error(
        "La lectura del descuento no coincide con los cambios. Revisa el listado antes de volver a guardar.",
      );
    setRow(result);
    const next = discountDraft(result);
    setForm(next);
    setBaseline(next);
    pending.current = null;
    setUnknown(false);
    setSaved(true);
    dispatch(requestTableRefresh());
  };
  const verify = async () => {
    if (lock.current || !id || !pending.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await confirm(id, pending.current);
    } catch (e) {
      setError(e.message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    if (lock.current || unknown || !form) return;
    let expected;
    try {
      expected = discountPayload(form, row);
    } catch (e) {
      setError(e.message);
      return;
    }
    lock.current = true;
    setBusy(true);
    setError("");
    setSaved(false);
    let target = id;
    try {
      pending.current = expected;
      setUnknown(true);
      const response = target
        ? await updateDiscount(expected, target)
        : await newDiscount(expected);
      if (response?.error !== false)
        throw Error(
          "No se ha podido confirmar el guardado. Comprueba el listado antes de repetirlo.",
        );
      if (!target) {
        target = response.data?.discountId;
        if (!target)
          throw Error(
            "No se ha recibido el identificador del descuento creado. Comprueba el listado antes de repetirlo.",
          );
        setId(target);
      }
      await confirm(target, expected);
    } catch (e) {
      setError(e.message || "No se ha podido confirmar el guardado.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const input = (key, label, props = {}) => (
    <AdminInput
      id={`discount-editor-${key}`}
      label={label}
      value={form[key]}
      onChange={(value) => change(key, value)}
      {...props}
    />
  );
  return (
    <div
      className="discountReferenceEditor"
      aria-busy={busy}
      onKeyDownCapture={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
    >
      <header className="discountEditorHeading">
        <div>
          <h2 id="admin-editor-title">
            {id
              ? `Editar · ${row?.discountCode || "Descuento"}`
              : "Nuevo descuento"}
          </h2>
          <p>Código, vigencia y condiciones de uso.</p>
        </div>
        <button
          type="button"
          aria-label="Cerrar descuento"
          disabled={busy}
          onClick={close}
        >
          <LuX />
        </button>
      </header>
      <form onSubmit={save} className="discountEditorForm" noValidate>
        <div className="discountEditorBody">
          {error && (
            <div role="alert" className="discountEditorError">
              {error}
              {!form && (
                <button type="button" onClick={() => setRetry((n) => n + 1)}>
                  Reintentar
                </button>
              )}
              {unknown && id && (
                <button type="button" disabled={busy} onClick={verify}>
                  Comprobar guardado
                </button>
              )}
            </div>
          )}
          {!form && !error && <p role="status">Cargando descuento…</p>}
          {form && (
            <fieldset
              className="discountEditorFields"
              disabled={busy || unknown}
            >
              <div className="discountEditorIdentity">
                {input(
                  "discountCode",
                  <>
                    Código <span className="discountRequired">*</span>
                  </>,
                  { autoComplete: "off" },
                )}
                <div className="adminField">
                  <label htmlFor="discount-editor-type">Tipo</label>
                  <select
                    id="discount-editor-type"
                    value={form.type}
                    onChange={(e) => change("type", e.target.value)}
                  >
                    <option value="percentage">Porcentaje</option>
                    <option value="value">Importe fijo</option>
                    {!["percentage", "value"].includes(form.type) && (
                      <option value={form.type}>{form.type}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="discountEditorIdentity">
                {input("discount", "Valor", {
                  type: "number",
                  min: 1,
                  step: 1,
                })}
                {input("useLimit", "Usos máximos", {
                  type: "number",
                  min: 0,
                  step: 1,
                  placeholder: "Sin límite",
                })}
              </div>
              <div className="discountEditorIdentity">
                {input("initDate", "Válido desde", { type: "date" })}
                {input("finalDate", "Válido hasta", { type: "date" })}
              </div>
              <div className="adminField">
                <label htmlFor="discount-editor-channels">Canales</label>
                <select
                  id="discount-editor-channels"
                  disabled
                  aria-describedby="discount-channel-help"
                >
                  <option>Sin selección disponible</option>
                </select>
                <p id="discount-channel-help" className="fieldHelp">
                  La asignación por canal todavía no está disponible.
                </p>
              </div>
              <details className="discountAdvanced">
                <summary>Más opciones</summary>
                {input("name", "Nombre del descuento")}
                <div className="discountEditorIdentity">
                  {input("initTime", "Hora de inicio (UTC)", {
                    type: "time",
                    step: 1,
                  })}
                  {input("finalTime", "Hora de fin (UTC)", {
                    type: "time",
                    step: 1,
                  })}
                </div>
                <p>
                  Las fechas y horas se muestran en UTC. Se conservan si no las
                  modificas.
                </p>
                {input("customerUseLimit", "Usos por cliente", {
                  type: "number",
                  min: 0,
                  step: 1,
                  placeholder: "Sin límite",
                })}
                {input("minPurchaseValue", "Compra mínima (€)", {
                  type: "number",
                  min: 0,
                  step: 1,
                })}
                <label className="discountCumulative">
                  <input
                    type="checkbox"
                    checked={form.isCumulative}
                    onChange={(e) => change("isCumulative", e.target.checked)}
                  />
                  Este código es acumulable
                </label>
                <p>
                  Aplicable a:{" "}
                  {row?.applicableTo === "category"
                    ? "Categorías asociadas"
                    : row?.applicableTo && row.applicableTo !== "order"
                      ? row.applicableTo
                      : "Pedidos"}
                  . Se conserva el ámbito y sus asociaciones.
                </p>
              </details>
            </fieldset>
          )}
        </div>
        <footer className="discountEditorFooter">
          <span role="status">
            {busy
              ? "Guardando…"
              : saved
                ? "Descuento guardado y comprobado"
                : unknown
                  ? "Guardado pendiente de comprobar"
                  : ""}
          </span>
          <button type="button" disabled={busy} onClick={close}>
            Cancelar
          </button>
          <button
            type="submit"
            className="discountPrimary"
            disabled={!form || busy || unknown || (id && !dirty)}
          >
            Guardar descuento
          </button>
        </footer>
      </form>
      {discard && (
        <EditorDialog
          title="¿Descartar los cambios?"
          description="Los cambios sin guardar se perderán."
          onClose={() => setDiscard(false)}
          footer={
            <>
              <button type="button" onClick={() => setDiscard(false)}>
                Seguir editando
              </button>
              <button type="button" onClick={finish}>
                Descartar cambios
              </button>
            </>
          }
        />
      )}
    </div>
  );
};
